import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { renderSchoolCarrierCard } from "../components/school-carrier-card";
import type { SchoolSettings, DepartmentGroup, Department } from "../types";
import { esc } from "../utils/html";

function methodColor(method: string): string {
  switch (method) {
    case "GET": return "#22c55e";
    case "POST": return "#3b82f6";
    case "PUT": return "#f59e0b";
    case "DELETE": return "#ef4444";
    default: return "#6b7280";
  }
}

function authBadge(): string {
  return `<span class="api-auth-badge api-auth-tenant">JWT + Mandant</span>`;
}

interface ApiEndpoint {
  method: string; path: string; description: string;
  body?: string; response?: string; note?: string; curl?: string;
}
interface ApiGroup { group: string; description: string; endpoints: ApiEndpoint[]; }

function renderApiSection(): string {
  const baseUrl = (window as any).__API_URL__ || window.location.origin;
  const kcUrl = (window as any).__KEYCLOAK_URL__ || window.location.origin;
  const tokenEndpoint = `${kcUrl}/realms/mediskill/protocol/openid-connect/token`;

  const groups: ApiGroup[] = [
    {
      group: "Klassen",
      description: "Klassen verwalten – Voraussetzung für Schüler-Import (kein CSV-Import)",
      endpoints: [
        { method: "GET", path: "/api/klassen", description: "Alle Klassen abrufen", response: `[{ "id": 1, "name": "PTA 2024" }]` },
        { method: "POST", path: "/api/klassen", description: "Klasse anlegen", body: `{ "name": "PTA 2024" }`, response: `{ "id": 1, "name": "PTA 2024" }` },
        { method: "PUT", path: "/api/klassen/:id", description: "Klasse bearbeiten", body: `{ "name": "PTA 2025" }` },
        { method: "DELETE", path: "/api/klassen/:id", description: "Klasse löschen" },
      ],
    },
    {
      group: "Schüler",
      description: "Schüler einzeln per REST oder per CSV-Massenimport anlegen",
      endpoints: [
        { method: "GET", path: "/api/schueler", description: "Alle Schüler abrufen (optional ?classId=)", response: `[{ "id": 1, "firstName": "Max", "lastName": "Muster", ... }]` },
        { method: "POST", path: "/api/schueler", description: "Schüler anlegen", body: `{ "firstName": "Max", "lastName": "Muster", "email": "max@example.com", "phone": "...", "street": "...", "houseNo": "...", "plz": "...", "ort": "...", "classId": 1, "hasCar": false }`, response: `{ "id": 5, "firstName": "Max", ... }` },
        { method: "PUT", path: "/api/schueler/:id", description: "Schüler bearbeiten (gleiche Felder wie POST)" },
        { method: "DELETE", path: "/api/schueler/:id", description: "Schüler löschen" },
        { method: "GET", path: "/api/schueler/csv-template", description: "CSV-Vorlage herunterladen", note: "Liefert eine leere CSV-Datei mit den benötigten Spaltenköpfen." },
        { method: "POST", path: "/api/schueler/csv-import", description: "Schüler per CSV-Massenimport", body: "multipart/form-data, Feld: file (CSV-Datei)", response: `{ "success": true, "imported": 42, "skipped": 2, "skippedRows": [...] }` },
      ],
    },
    {
      group: "Praktikumsstellen",
      description: "Praktikumsstellen einzeln per REST oder per CSV-Massenimport anlegen",
      endpoints: [
        { method: "GET", path: "/api/praktikumsstellen", description: "Alle Praktikumsstellen abrufen (optional ?department=)", response: `[{ "id": 1, "name": "Praxis Muster", "capacities": { "PT": 2 }, ... }]` },
        { method: "POST", path: "/api/praktikumsstellen", description: "Praktikumsstelle anlegen", body: `{ "name": "Praxis Muster", "plz": "...", "ort": "...", "street": "...", "houseNo": "...", "category": "PRAXIS", "capacities": { "PT": 2 }, "leadingContact": "...", ... }`, response: `{ "id": 10, "name": "Praxis Muster", ... }` },
        { method: "PUT", path: "/api/praktikumsstellen/:id", description: "Praktikumsstelle bearbeiten (gleiche Felder wie POST)" },
        { method: "DELETE", path: "/api/praktikumsstellen/:id", description: "Praktikumsstelle löschen" },
        { method: "GET", path: "/api/praktikumsstellen/csv-template", description: "CSV-Vorlage herunterladen", note: "Spalten sind dynamisch – abhängig von den Fachbereichen der Schule." },
        { method: "POST", path: "/api/praktikumsstellen/csv-import", description: "Praktikumsstellen per CSV-Massenimport", body: "multipart/form-data, Feld: file (CSV-Datei)", response: `{ "success": true, "imported": 15, "skipped": 1, "skippedRows": [...] }` },
      ],
    },
  ];

  const groupsHtml = groups.map(g => `
    <div class="api-group">
      <div class="api-group-header">
        <div>
          <h3 class="api-group-title">${esc(g.group)}</h3>
          <div class="api-group-desc">${esc(g.description)}</div>
        </div>
        ${authBadge()}
      </div>
      <div class="api-endpoints">
        ${g.endpoints.map(e => `
          <details class="api-endpoint">
            <summary class="api-endpoint-summary">
              <span class="api-method" style="background:${methodColor(e.method)}">${e.method}</span>
              <code class="api-path">${esc(e.path)}</code>
              <span class="api-desc">${esc(e.description)}</span>
            </summary>
            <div class="api-endpoint-detail">
              <table class="api-detail-table">
                <tr><td class="api-detail-label">Authentifizierung</td><td>${authBadge()}</td></tr>
                ${e.curl ? `<tr><td class="api-detail-label">Beispiel (cURL)</td><td><code class="api-code-block" style="white-space:pre-wrap">${esc(e.curl)}</code></td></tr>` : ""}
                ${e.body ? `<tr><td class="api-detail-label">Request-Body</td><td><code class="api-code-block">${esc(e.body)}</code></td></tr>` : ""}
                ${e.response ? `<tr><td class="api-detail-label">Response</td><td><code class="api-code-block">${esc(e.response)}</code></td></tr>` : ""}
                ${e.note ? `<tr><td class="api-detail-label">Hinweis</td><td style="font-size:.8125rem;color:var(--gray-600)">${esc(e.note)}</td></tr>` : ""}
              </table>
            </div>
          </details>
        `).join("")}
      </div>
    </div>
  `).join("");

  return `
    <div class="card" style="padding:1.5rem">
      <details>
        <summary style="cursor:pointer;list-style:none;display:flex;align-items:center;justify-content:space-between;gap:1rem">
          <div>
            <h2 style="font-size:1rem;font-weight:600;margin:0">API-Informationen</h2>
            <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.25rem">Endpunkte für automatisierten Import und Datenpflege</div>
          </div>
          <span class="api-auth-badge api-auth-tenant" style="flex-shrink:0">Ausklappen ↓</span>
        </summary>

        <div style="margin-top:1.25rem">
          <!-- Basis-URL -->
          <div style="margin-bottom:1rem;padding:.75rem 1rem;background:var(--gray-50);border:1px solid var(--gray-200);border-radius:.375rem;font-size:.8125rem">
            <span style="font-weight:600;color:var(--gray-700)">Basis-URL:</span>
            <code class="api-code-block" style="margin-left:.5rem">${esc(baseUrl)}</code>
          </div>

          <!-- Auth-Erklärung -->
          <div style="margin-bottom:1.25rem;padding:.875rem 1rem;background:var(--primary-50,#eff6ff);border:1px solid var(--primary-200,#bfdbfe);border-radius:.375rem;font-size:.8125rem;color:var(--gray-700);line-height:1.6">
            <div style="font-weight:600;margin-bottom:.5rem">Authentifizierung – Bearer Token</div>
            <p style="margin:0 0 .5rem">Alle Endpunkte erfordern einen Access-Token im <code class="api-code-block">Authorization</code>-Header. Der Token ist an dein Schulkonto gebunden – alle Daten werden automatisch deiner Schule zugeordnet, ohne zusätzlichen Parameter.</p>

            <div style="font-weight:600;margin:.625rem 0 .25rem">Token per Zugangsdaten holen (cURL):</div>
            <code class="api-code-block" style="display:block;white-space:pre-wrap;margin-bottom:.5rem">curl -s -X POST "${esc(tokenEndpoint)}" \\
  -d "grant_type=password&amp;client_id=mediskill-app&amp;username=IHRE_EMAIL&amp;password=IHR_PASSWORT" \\
  | jq -r '.access_token'</code>

            <div style="font-weight:600;margin:.25rem 0 .25rem">Token im Request verwenden – Beispiel GET /api/klassen:</div>
            <code class="api-code-block" style="display:block;white-space:pre-wrap;margin-bottom:.375rem">curl -s "${esc(baseUrl)}/api/klassen" \\
  -H "Authorization: Bearer &lt;access_token&gt;"</code>

            <p style="margin:.375rem 0 0;color:var(--gray-500)">Tokens sind ca. 5 Minuten gültig. Für längere Sitzungen kann der Token über denselben Endpunkt mit <code class="api-code-block">grant_type=refresh_token</code> erneuert werden.</p>
          </div>

          <!-- Endpunkt-Gruppen -->
          ${groupsHtml}
        </div>
      </details>
    </div>`;
}

let settings: SchoolSettings | null = null;
let allGroups: DepartmentGroup[] = [];
let currentEl: HTMLElement | null = null;

async function loadSettings() {
  [settings, allGroups] = await Promise.all([api.settings.get(), api.settings.groupsWithOverrides()]);
}

function captureOpenGroups(el: HTMLElement): Set<string> {
  return new Set(
    Array.from(el.querySelectorAll<HTMLDetailsElement>('details[data-group-id][open]'))
      .map(d => d.dataset.groupId!)
  );
}

function restoreOpenGroups(el: HTMLElement, openGroupIds: Set<string>): void {
  el.querySelectorAll<HTMLDetailsElement>('details[data-group-id]').forEach(d => {
    if (openGroupIds.has(d.dataset.groupId!)) d.open = true;
  });
}


async function openGroupDeactivationModal(
  impact: { assignmentCount: number; affectedClasses: { id: number; name: string }[]; otherGroups: { id: number; name: string; isCustom?: boolean }[] },
  _isCustom: boolean
): Promise<{ reassignToGroupId: number | null; reassignIsCustom: boolean } | null> {
  return new Promise(resolve => {
    const assignWarn = impact.assignmentCount > 0
      ? `<div style="background:var(--danger-light,#fee2e2);border:1px solid #fca5a5;border-radius:.5rem;padding:.75rem 1rem;margin:.75rem 0">
           <strong style="color:var(--danger,#dc2626)">⚠ ${impact.assignmentCount} Einsatzplan-${impact.assignmentCount === 1 ? "Eintrag wird" : "Einträge werden"} unwiderruflich gelöscht.</strong>
         </div>`
      : "";

    const classSection = impact.affectedClasses.length > 0
      ? `<div style="margin:.75rem 0">
           <p style="margin:0 0 .5rem 0;font-size:.9rem">${impact.affectedClasses.length} ${impact.affectedClasses.length === 1 ? "Klasse ist" : "Klassen sind"} dieser Gruppe zugewiesen. Neue Fachbereichsgruppe für diese Klassen:</p>
           <select id="deact-reassign" style="width:100%;padding:.5rem;border:1px solid var(--gray-300);border-radius:.375rem;font-size:.9rem">
             <option value="">– Ohne Fachbereichsgruppe lassen –</option>
             ${impact.otherGroups.map(g => `<option value="${g.isCustom ? "c" : "p"}-${g.id}">${esc(g.name)}</option>`).join("")}
           </select>
         </div>`
      : "";

    openModal("Gruppe deaktivieren", `
      <p style="margin:0 0 .75rem 0">Die Gruppe wird deaktiviert und erscheint nicht mehr in der Planung, auf der Karte und in der Wunschliste.</p>
      ${assignWarn}
      ${classSection}
      <div class="form-actions">
        <button class="btn btn-secondary" id="deact-cancel">Abbrechen</button>
        <button class="btn btn-danger" id="deact-confirm">Gruppe deaktivieren</button>
      </div>`);
    const mb = modalBody();
    mb.querySelector("#deact-cancel")!.addEventListener("click", () => { closeModal(); resolve(null); });
    mb.querySelector("#deact-confirm")!.addEventListener("click", () => {
      const sel = mb.querySelector<HTMLSelectElement>("#deact-reassign");
      const raw = sel?.value ?? "";
      const reassignIsCustom = raw.startsWith("c-");
      const reassignToGroupId = raw ? (+raw.slice(2) || null) : null;
      closeModal();
      resolve({ reassignToGroupId, reassignIsCustom });
    });
  });
}

function renderGroupPoolSection(group: DepartmentGroup): string {
  const poolHours = group.poolHours!;
  const rows = group.departments.filter(d => d.active !== false).map(d => `
    <tr>
      <td style="padding:.5rem;display:flex;align-items:center;gap:.5rem">
        <span style="display:inline-block;width:.875rem;height:.875rem;border-radius:.125rem;background:${esc(d.color)};flex-shrink:0"></span>
        ${esc(d.nameDe)}
      </td>
      <td style="padding:.5rem;color:var(--gray-500)">${d.requiredHours} h</td>
      <td style="padding:.5rem">
        <input type="number" class="pool-input-g" data-dept-id="${esc(d.id)}" data-group-id="${group.id}"
          value="${d.poolHours ?? 0}" min="0"
          style="width:90px;padding:.25rem .5rem;border:1px solid var(--gray-300);border-radius:.375rem;font-size:.875rem" />
      </td>
      <td class="pool-total-cell-g" data-dept-id="${esc(d.id)}" data-group-id="${group.id}"
        style="padding:.5rem;color:var(--gray-600);font-weight:500">
        ${d.requiredHours + (d.poolHours ?? 0)} h
      </td>
    </tr>
  `).join("");

  return `
    <div style="margin-top:1.25rem;padding-top:1.25rem;border-top:1px solid var(--gray-200)">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:.75rem">
        <div>
          <h3 style="font-size:.9375rem;font-weight:600;margin:0">Pool-Stunden verteilen</h3>
          <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.125rem">
            Diese Gruppe hat <strong>${poolHours} h</strong> zur Verteilung auf die Fachbereiche.
          </div>
        </div>
        <div id="pool-counter-g-${group.id}" style="font-size:.875rem;font-weight:600;padding:.25rem .75rem;border-radius:999px;background:var(--gray-100);color:var(--gray-600)">
          0 / ${poolHours} h
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;margin-bottom:.75rem">
        <thead>
          <tr style="border-bottom:1px solid var(--gray-200);font-size:.8125rem;color:var(--gray-500)">
            <th style="padding:.5rem;font-weight:500;text-align:left">Fachbereich</th>
            <th style="padding:.5rem;font-weight:500;text-align:left">Basis</th>
            <th style="padding:.5rem;font-weight:500;text-align:left">Zu verteilen</th>
            <th style="padding:.5rem;font-weight:500;text-align:left">Gesamt</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="display:flex;align-items:center;justify-content:flex-end">
        <button class="btn btn-primary btn-save-pool-g" data-group-id="${group.id}" disabled>Pool-Verteilung speichern</button>
      </div>
    </div>`;
}

function groupIsActive(group: DepartmentGroup): boolean {
  // Standardgruppen haben einen gespeicherten Status, neue starten ausgeschaltet
  if (!group.isCustom && group.isActive !== undefined) return group.isActive;
  return group.departments.length === 0 || group.departments.every(d => d.active !== false);
}

function renderGroupAccordion(group: DepartmentGroup): string {
  if (group.isCustom) return renderCustomGroupAccordion(group);
  const active = groupIsActive(group);
  // Standardgruppen bestehen aus den Katalog-Fachbereichen plus den eigenen
  // Ergänzungen dieser Schule. Nur die eigenen lassen sich entfernen.
  const standardDepts = group.departments.filter(d => !d.isOwn);
  const ownDepts = group.departments.filter(d => d.isOwn);

  const deptRows = standardDepts.map(d => `
    <tr>
      <td style="padding:.5rem .5rem .5rem 0;width:2rem">
        <span style="display:inline-block;width:1.25rem;height:1.25rem;border-radius:.25rem;background:${esc(d.color)};vertical-align:middle"></span>
      </td>
      <td style="padding:.5rem;font-weight:500">${esc(d.nameDe)}</td>
      <td style="padding:.5rem;color:var(--gray-500)">${esc(d.shortName)}</td>
      <td style="padding:.5rem;color:var(--gray-600)">${d.requiredHours > 0 ? `${d.requiredHours} h` : "–"}</td>
      <td style="padding:.5rem;text-align:right">
        <button class="btn btn-sm btn-secondary btn-edit-std-dept" data-dept-id="${esc(d.id)}" data-group-id="${group.id}">Bearbeiten</button>
      </td>
    </tr>
  `).join("");

  // Bewusst dieselbe Tabelle wie die Standard-Fachbereiche, nur durch eine
  // Zwischenüberschrift getrennt: zwei Tabellen würden in den Spalten auseinanderlaufen.
  const ownRows = ownDepts.length === 0 ? "" : `
    <tr>
      <td colspan="5" style="padding:1.25rem .5rem .25rem 0;font-size:.8125rem;font-weight:600;color:var(--gray-500)">
        Eigene Fachbereiche dieser Schule
      </td>
    </tr>` + ownDepts.map(d => `
    <tr>
      <td style="padding:.5rem .5rem .5rem 0;width:2rem">
        <span style="display:inline-block;width:1.25rem;height:1.25rem;border-radius:.25rem;background:${esc(d.color)};vertical-align:middle"></span>
      </td>
      <td style="padding:.5rem;font-weight:500">${esc(d.nameDe)}</td>
      <td style="padding:.5rem;color:var(--gray-500)">${esc(d.shortName)}</td>
      <td style="padding:.5rem;color:var(--gray-600)">${d.requiredHours > 0 ? `${d.requiredHours} h` : "–"}</td>
      <td style="padding:.5rem;text-align:right;white-space:nowrap">
        <button class="btn btn-sm btn-secondary btn-edit-std-dept" data-dept-id="${esc(d.id)}" data-group-id="${group.id}" style="margin-right:.25rem">Bearbeiten</button>
        <button class="btn btn-sm btn-danger btn-delete-custom-dept" data-dept-id="${esc(d.id)}" data-group-id="${group.id}">Entfernen</button>
      </td>
    </tr>
  `).join("");

  const poolSection = (active && group.poolHours != null && group.poolHours > 0) ? renderGroupPoolSection(group) : "";

  return `
    <details data-group-id="${group.id}" style="border:1px solid var(--gray-200);border-radius:.5rem;overflow:hidden${!active ? ";opacity:.6" : ""}">
      <summary style="cursor:pointer;list-style:none;display:flex;align-items:center;justify-content:space-between;gap:.75rem;padding:.75rem 1rem;background:var(--gray-50)">
        <div style="font-weight:500;font-size:.9375rem">${esc(group.name)}</div>
        <div style="display:flex;align-items:center;gap:.75rem">
          <label class="group-toggle-switch" onclick="event.stopPropagation()">
            <input type="checkbox" class="group-active-toggle" data-group-id="${group.id}" data-is-custom="0"
              ${active ? "checked" : ""} />
            <span class="group-toggle-track"><span class="group-toggle-knob"></span></span>
            <span class="group-toggle-label">${active ? "Aktiv" : "Inaktiv"}</span>
          </label>
          <span style="font-size:.8125rem;color:var(--gray-400)">${group.departments.length} Fachbereiche ▼</span>
        </div>
      </summary>
      <div style="padding:1rem">
        <table style="width:100%;border-collapse:collapse">
          <thead>
            <tr style="border-bottom:1px solid var(--gray-200);font-size:.8125rem;color:var(--gray-500)">
              <th style="padding:.5rem .5rem .5rem 0;width:2rem"></th>
              <th style="padding:.5rem;font-weight:500;text-align:left">Name</th>
              <th style="padding:.5rem;font-weight:500;text-align:left">Kürzel</th>
              <th style="padding:.5rem;font-weight:500;text-align:left">Pflichtstunden</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${deptRows}${ownRows}</tbody>
        </table>

        <div style="margin-top:1rem">
          ${ownDepts.length === 0 ? `<p style="font-size:.875rem;color:var(--gray-400);margin:0 0 .75rem">Diese Gruppe enthält bisher nur die Standard-Fachbereiche.</p>` : ""}
          <button class="btn btn-sm btn-secondary btn-add-extension-dept" data-group-id="${group.id}">+ Eigenen Fachbereich hinzufügen</button>
        </div>

        ${poolSection}
      </div>
    </details>`;
}

function renderCustomGroupAccordion(group: DepartmentGroup): string {
  const active = groupIsActive(group);
  const deptRows = group.departments.map(d => `
    <tr>
      <td style="padding:.5rem .5rem .5rem 0;width:2rem">
        <span style="display:inline-block;width:1.25rem;height:1.25rem;border-radius:.25rem;background:${esc(d.color)};vertical-align:middle"></span>
      </td>
      <td style="padding:.5rem;font-weight:500">${esc(d.nameDe)}</td>
      <td style="padding:.5rem;color:var(--gray-500)">${esc(d.shortName)}</td>
      <td style="padding:.5rem;color:var(--gray-600)">${d.requiredHours > 0 ? `${d.requiredHours} h` : "–"}</td>
      <td style="padding:.5rem;text-align:right;white-space:nowrap">
        <button class="btn btn-sm btn-secondary btn-edit-std-dept" data-dept-id="${esc(d.id)}" data-group-id="${group.id}" style="margin-right:.25rem">Bearbeiten</button>
        <button class="btn btn-sm btn-danger btn-delete-custom-dept" data-dept-id="${esc(d.id)}" data-group-id="${group.id}">Entfernen</button>
      </td>
    </tr>
  `).join("");

  return `
    <details data-group-id="custom-${group.id}" style="border:1px solid var(--gray-200);border-radius:.5rem;overflow:hidden${!active ? ";opacity:.6" : ""}">
      <summary style="cursor:pointer;list-style:none;display:flex;align-items:center;justify-content:space-between;gap:.75rem;padding:.75rem 1rem;background:var(--gray-50)">
        <div style="font-weight:500;font-size:.9375rem">${esc(group.name)}</div>
        <div style="display:flex;align-items:center;gap:.5rem">
          <label class="group-toggle-switch" onclick="event.stopPropagation()">
            <input type="checkbox" class="group-active-toggle" data-group-id="${group.id}" data-is-custom="1"
              ${active ? "checked" : ""} />
            <span class="group-toggle-track"><span class="group-toggle-knob"></span></span>
            <span class="group-toggle-label">${active ? "Aktiv" : "Inaktiv"}</span>
          </label>
          <span style="font-size:.8125rem;color:var(--gray-400)">${group.departments.length} Fachbereiche ▼</span>
          <button class="btn btn-sm btn-secondary btn-rename-custom-group" data-group-id="${group.id}" onclick="event.stopPropagation()">Umbenennen</button>
          <button class="btn btn-sm btn-danger btn-delete-custom-group" data-group-id="${group.id}" onclick="event.stopPropagation()">Löschen</button>
        </div>
      </summary>
      <div style="padding:1rem">
        ${group.departments.length > 0 ? `
        <table style="width:100%;border-collapse:collapse;margin-bottom:.75rem">
          <thead>
            <tr style="border-bottom:1px solid var(--gray-200);font-size:.8125rem;color:var(--gray-500)">
              <th style="padding:.5rem .5rem .5rem 0;width:2rem"></th>
              <th style="padding:.5rem;font-weight:500;text-align:left">Name</th>
              <th style="padding:.5rem;font-weight:500;text-align:left">Kürzel</th>
              <th style="padding:.5rem;font-weight:500;text-align:left">Pflichtstunden</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${deptRows}</tbody>
        </table>` : `<p style="font-size:.875rem;color:var(--gray-400);margin-bottom:.75rem">Noch keine Fachbereiche in dieser Gruppe.</p>`}
        <button class="btn btn-sm btn-secondary btn-add-custom-dept" data-group-id="${group.id}">+ Fachbereich hinzufügen</button>
      </div>
    </details>`;
}

function renderSettings(el: HTMLElement) {
  if (!settings) return;

  el.innerHTML = `
    <div class="settings-page-header">
      <div>
        <h1>Allgemein</h1>
        <p>Schuldaten und Fachbereiche verwalten.</p>
      </div>
    </div>

    <div style="display:grid;gap:1.5rem">
      <!-- Schuldaten -->
      <div class="card" style="padding:1.5rem">
        <h2 style="font-size:1rem;font-weight:600;margin-bottom:1rem">Schuldaten</h2>
        <div class="form-grid">
          <div class="form-group col-span-2">
            <label>Schulname</label>
            <input id="s-name" value="${esc(settings.name)}" disabled style="background:var(--gray-50);color:var(--gray-500)" />
          </div>
          <div class="form-group col-span-2">
            <label>Adresse</label>
            <input id="s-address" value="${esc(settings.address || "")}" placeholder="Straße und Hausnummer" />
          </div>
          <div class="form-group">
            <label>PLZ</label>
            <input id="s-plz" value="${esc(settings.plz || "")}" placeholder="PLZ" maxlength="10" />
          </div>
          <div class="form-group">
            <label>Ort</label>
            <input id="s-ort" value="${esc(settings.ort || "")}" placeholder="Ort" />
          </div>
          <div class="form-group">
            <label>Telefon</label>
            <input id="s-phone" value="${esc(settings.phone || "")}" placeholder="Telefonnummer" />
          </div>
          <div class="form-group">
            <label>E-Mail</label>
            <input id="s-email" value="${esc(settings.email || "")}" placeholder="E-Mail-Adresse" type="email" />
          </div>
        </div>
        <div style="display:flex;justify-content:flex-end;margin-top:1rem">
          <button class="btn btn-primary" id="btn-save-school">Speichern</button>
        </div>
      </div>

      <!-- Fachbereiche pro Gruppe -->
      <div class="card" style="padding:1.5rem">
        <div style="margin-bottom:1rem">
          <h2 style="font-size:1rem;font-weight:600;margin:0">Fachbereiche pro Gruppe</h2>
          <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.25rem">Standard-Fachbereiche anpassen und um eigene ergänzen. Änderungen gelten nur für diese Schule.</div>
        </div>
        <div style="display:grid;gap:.75rem">
          ${allGroups.map(g => renderGroupAccordion(g)).join("")}
          <button class="btn btn-secondary" id="btn-add-custom-group" style="width:fit-content">+ Individuelle Gruppe hinzufügen</button>
        </div>
      </div>

      <!-- Individuelle Einstellungen (nur wenn mindestens eine Einstellung verfügbar) -->
      ${(() => {
        const kpzAvailable = !!settings.planungEnabled;
        const vorplanungAvailable = !!settings.planungEnabled;
        const abrechnungAvailable = !!settings.planungEnabled;
        // Weitere zukünftige Einstellungen hier hinzufügen: const xyzAvailable = !!settings.xyzEnabled;
        const hasAnyIndividualSetting = kpzAvailable || vorplanungAvailable || abrechnungAvailable;
        if (!hasAnyIndividualSetting) return "";
        return `
      <div class="card" style="padding:1.5rem">
        <h2 style="font-size:1rem;font-weight:600;margin-bottom:.75rem">Individuelle Einstellungen</h2>
        <div style="font-size:.8125rem;color:var(--gray-500);margin-bottom:1rem">Erweiterte Funktionen für die Praktikumsplanung</div>
        <div style="display:flex;flex-direction:column;gap:.75rem">
          ${kpzAvailable ? `<label style="display:flex;align-items:flex-start;gap:.75rem;cursor:pointer">
            <input type="checkbox" id="toggle-kpz" ${settings.kapazitaetProZeitraumEnabled ? "checked" : ""}
              style="width:16px;height:16px;margin-top:2px;accent-color:var(--primary);flex-shrink:0" />
            <div>
              <div style="font-weight:500;font-size:.875rem">Kapazität pro Zeitraum</div>
              <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.25rem">
                Erlaubt eine zusätzliche Begrenzung, wie viele verschiedene Schüler pro Planungszeitraum
                in einer Praktikumsstelle eingesetzt werden können – unabhängig von den gleichzeitig belegten Plätzen.
              </div>
            </div>
          </label>` : ""}
          ${vorplanungAvailable ? `<div>
            <label style="display:flex;align-items:flex-start;gap:.75rem;cursor:pointer">
              <input type="checkbox" id="toggle-vorplanung" ${settings.vorplanungEnabled ? "checked" : ""}
                style="width:16px;height:16px;margin-top:2px;accent-color:var(--primary);flex-shrink:0" />
              <div>
                <div style="font-weight:500;font-size:.875rem">Vorplanung</div>
                <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.25rem">
                  Erlaubt es, Einsätze zunächst nicht sichtbar zu planen und erst einzeln oder gesammelt pro
                  Zeitraum für Schüler freizugeben. Zusätzlich können freigegebene Einsätze als vorläufig markiert
                  werden, um Schülern zu zeigen, dass sich an diesem Einsatz noch etwas ändern könnte.
                </div>
              </div>
            </label>
            ${settings.vorplanungEnabled ? `<div style="margin:.5rem 0 0 2.25rem;padding:.75rem;background:var(--gray-50);border-radius:.5rem;display:flex;flex-direction:column;gap:.5rem">
              <div style="font-size:.8125rem;font-weight:500;color:var(--gray-700)">Standardwerte für neue, manuell angelegte Einsätze</div>
              <label style="display:flex;align-items:center;gap:.5rem;font-size:.8125rem;cursor:pointer">
                <input type="checkbox" id="toggle-vorplanung-default-visible" ${settings.vorplanungDefaultVisible ? "checked" : ""}
                  style="width:14px;height:14px;accent-color:var(--primary)" />
                Für Schüler sichtbar
              </label>
              <label style="display:flex;align-items:center;gap:.5rem;font-size:.8125rem;cursor:pointer">
                <input type="checkbox" id="toggle-vorplanung-default-confirmed" ${!settings.vorplanungDefaultPreliminary ? "checked" : ""}
                  style="width:14px;height:14px;accent-color:var(--primary)" />
                Bestätigt
              </label>
              <div style="font-size:.75rem;color:var(--gray-400)">Gilt nur für manuell angelegte Einsätze, nicht für die Automatische Planung.</div>
            </div>` : ""}
          </div>` : ""}
          ${abrechnungAvailable ? `<label style="display:flex;align-items:flex-start;gap:.75rem;cursor:pointer">
            <input type="checkbox" id="toggle-abrechnung" ${settings.abrechnungEnabled ? "checked" : ""}
              style="width:16px;height:16px;margin-top:2px;accent-color:var(--primary);flex-shrink:0" />
            <div>
              <div style="font-weight:500;font-size:.875rem">Abrechnung</div>
              <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.25rem">
                Ermöglicht, einzelne Einsätze als „abgerechnet" zu markieren, sobald die Praktikumsstelle
                eine Rechnung gestellt hat. Das Kennzeichen erscheint im Planungs-Grid und im Excel-Export.
              </div>
            </div>
          </label>` : ""}
        </div>
      </div>`;
      })()}

      <!-- Schulträger (wird nachgeladen) -->
      <div id="school-carrier-card"></div>

      <!-- API-Informationen -->
      ${renderApiSection()}

      <!-- Datenexport & Schule löschen -->
      <div class="card" style="padding:1.5rem">
        <details id="advanced-details">
          <summary style="cursor:pointer;list-style:none;display:flex;align-items:center;justify-content:space-between;gap:1rem">
            <div>
              <h2 style="font-size:1rem;font-weight:600;margin:0">Erweiterte Optionen</h2>
              <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.25rem">Datenexport und Schule löschen</div>
            </div>
            <span class="api-auth-badge api-auth-tenant" style="flex-shrink:0">Ausklappen ↓</span>
          </summary>
          <div style="display:flex;flex-direction:column;gap:1.5rem;margin-top:1.5rem">
            <div>
              <h3 style="font-size:.9375rem;font-weight:600;margin-bottom:.25rem">Datenexport</h3>
              <div style="font-size:.8125rem;color:var(--gray-500);margin-bottom:1rem">Alle Schuldaten als ZIP-Archiv herunterladen – enthält CSV-Dateien für Schüler, Klassen, Praktikumsstellen, Planung und mehr.</div>
              <button class="btn btn-secondary" id="btn-export-data">Daten exportieren</button>
            </div>
            <hr style="border:none;border-top:1px solid var(--gray-200);margin:0" />
            <div id="delete-school-details">
              <h3 style="font-size:.9375rem;font-weight:600;margin-bottom:.25rem">Schule löschen</h3>
              <div style="font-size:.8125rem;color:var(--gray-500);margin-bottom:1rem">Schule und alle Daten unwiderruflich löschen</div>
              <div id="delete-school-content"></div>
            </div>
          </div>
        </details>
      </div>
    </div>`;

  // Event-Listener
  el.querySelector("#btn-save-school")!.addEventListener("click", saveSchoolData);

  // Datenexport
  el.querySelector("#btn-export-data")!.addEventListener("click", async () => {
    const btn = el.querySelector("#btn-export-data") as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "Wird exportiert…";
    try {
      await api.settings.exportData();
    } catch (err: any) {
      showToast("Export fehlgeschlagen: " + err.message);
    } finally {
      btn.disabled = false;
      btn.textContent = "Daten exportieren";
    }
  });

  // Standard-Fachbereich (aus Gruppe) bearbeiten
  el.querySelectorAll(".btn-edit-std-dept").forEach(btn => {
    btn.addEventListener("click", () => {
      const deptId = (btn as HTMLElement).dataset.deptId!;
      let dept;
      for (const g of allGroups) {
        dept = g.departments.find(d => d.id === deptId);
        if (dept) break;
      }
      if (dept) openDepartmentEditModal(dept);
    });
  });

  // Per-Gruppe Pool-Verteilung
  allGroups.filter(g => g.poolHours != null && g.poolHours > 0).forEach(g => {
    attachGroupPoolListeners(el, g);
  });

  // Active-Toggle pro Gruppe
  el.querySelectorAll<HTMLInputElement>(".group-active-toggle").forEach(cb => {
    cb.addEventListener("change", async () => {
      const groupId = +cb.dataset.groupId!;
      const isCustom = cb.dataset.isCustom === "1";
      const active = cb.checked;
      const setLabel = (checked: boolean) => {
        const lbl = cb.closest("label")?.querySelector(".group-toggle-label");
        if (lbl) lbl.textContent = checked ? "Aktiv" : "Inaktiv";
      };

      if (!active) {
        // Bestätigungs-Modal mit Impact-Info anzeigen
        let impact;
        try {
          impact = await api.settings.getGroupDeactivationImpact(groupId, isCustom);
        } catch (err: any) {
          cb.checked = true; setLabel(true);
          alert(err.message);
          return;
        }
        const result = await openGroupDeactivationModal(impact, isCustom);
        if (!result) { cb.checked = true; setLabel(true); return; }
        try {
          await api.settings.setGroupActive(groupId, isCustom, false, {
            reassignClassesToGroupId: result.reassignToGroupId ?? undefined,
            reassignIsCustom: result.reassignIsCustom,
          });
        } catch (err: any) {
          cb.checked = true; setLabel(true);
          alert(err.message);
          return;
        }
      } else {
        try {
          await api.settings.setGroupActive(groupId, isCustom, true);
        } catch (err: any) {
          cb.checked = false; setLabel(false);
          alert(err.message);
          return;
        }
      }

      const openGroupIds = captureOpenGroups(el);
      await loadSettings();
      renderSettings(el);
      restoreOpenGroups(el, openGroupIds);
    });
  });

  // Individuelle Gruppe anlegen
  el.querySelector("#btn-add-custom-group")?.addEventListener("click", () => {
    openModal("Individuelle Gruppe erstellen", `
      <div class="form-group"><label>Name</label><input id="cg-name" type="text" placeholder="z. B. Logopädie" /></div>
      <div class="form-actions">
        <button class="btn btn-secondary" id="cg-cancel">Abbrechen</button>
        <button class="btn btn-primary" id="cg-save">Erstellen</button>
      </div>`);
    const mb = modalBody();
    mb.querySelector("#cg-cancel")!.addEventListener("click", closeModal);
    mb.querySelector("#cg-save")!.addEventListener("click", async () => {
      const name = (mb.querySelector("#cg-name") as HTMLInputElement).value.trim();
      if (!name) return;
      try {
        await api.settings.createCustomGroup(name);
        closeModal();
        const openGroupIds = captureOpenGroups(el);
        await loadSettings();
        renderSettings(el);
        restoreOpenGroups(el, openGroupIds);
      } catch (err: any) { alert(err.message); }
    });
  });

  // Individuelle Gruppe umbenennen
  el.querySelectorAll<HTMLButtonElement>(".btn-rename-custom-group").forEach(btn => {
    btn.addEventListener("click", () => {
      const groupId = +btn.dataset.groupId!;
      const group = allGroups.find(g => g.id === groupId && g.isCustom);
      if (!group) return;
      openModal("Gruppe umbenennen", `
        <div class="form-group"><label>Name</label><input id="cg-rename" type="text" value="${esc(group.name)}" /></div>
        <div class="form-actions">
          <button class="btn btn-secondary" id="cg-rename-cancel">Abbrechen</button>
          <button class="btn btn-primary" id="cg-rename-save">Speichern</button>
        </div>`);
      const mb = modalBody();
      mb.querySelector("#cg-rename-cancel")!.addEventListener("click", closeModal);
      mb.querySelector("#cg-rename-save")!.addEventListener("click", async () => {
        const name = (mb.querySelector("#cg-rename") as HTMLInputElement).value.trim();
        if (!name) return;
        try {
          await api.settings.updateCustomGroup(groupId, name);
          closeModal();
          const openGroupIds = captureOpenGroups(el);
          await loadSettings();
          renderSettings(el);
          restoreOpenGroups(el, openGroupIds);
        } catch (err: any) { alert(err.message); }
      });
    });
  });

  // Individuelle Gruppe löschen
  el.querySelectorAll<HTMLButtonElement>(".btn-delete-custom-group").forEach(btn => {
    btn.addEventListener("click", async () => {
      const groupId = +btn.dataset.groupId!;
      const group = allGroups.find(g => g.id === groupId && g.isCustom);
      if (!group) return;
      if (!confirm(`Gruppe „${group.name}" löschen? Die Fachbereiche bleiben erhalten.`)) return;
      try {
        await api.settings.deleteCustomGroup(groupId);
        const openGroupIds = captureOpenGroups(el);
        await loadSettings();
        renderSettings(el);
        restoreOpenGroups(el, openGroupIds);
      } catch (err: any) { alert(err.message); }
    });
  });

  // Fachbereich zur individuellen Gruppe hinzufügen
  el.querySelectorAll<HTMLButtonElement>(".btn-add-custom-dept").forEach(btn => {
    btn.addEventListener("click", () => {
      const groupId = +btn.dataset.groupId!;
      openModal("Fachbereich hinzufügen", `
        ${deptFormHtml()}
        <div class="form-actions">
          <button class="btn btn-secondary" id="cd-cancel">Abbrechen</button>
          <button class="btn btn-primary" id="cd-save">Hinzufügen</button>
        </div>`);
      const mb = modalBody();
      mb.querySelector("#cd-cancel")!.addEventListener("click", closeModal);
      mb.querySelector("#cd-save")!.addEventListener("click", async () => {
        const values = getDeptFormValues();
        if (!values) return;
        try {
          await api.settings.addDepartment({ ...values, sortOrder: 0, customGroupId: groupId });
          closeModal();
          const openGroupIds = captureOpenGroups(el);
          await loadSettings();
          renderSettings(el);
          restoreOpenGroups(el, openGroupIds);
          showToast("Fachbereich hinzugefügt");
        } catch (err: any) { alert(err.message); }
      });
    });
  });

  // Eigenen Fachbereich zu einer Standardgruppe hinzufügen
  el.querySelectorAll<HTMLButtonElement>(".btn-add-extension-dept").forEach(btn => {
    btn.addEventListener("click", () => {
      const groupId = +btn.dataset.groupId!;
      openModal("Eigenen Fachbereich hinzufügen", `
        ${deptFormHtml()}
        <div class="form-actions">
          <button class="btn btn-secondary" id="ed-cancel">Abbrechen</button>
          <button class="btn btn-primary" id="ed-save">Hinzufügen</button>
        </div>`);
      const mb = modalBody();
      mb.querySelector("#ed-cancel")!.addEventListener("click", closeModal);
      mb.querySelector("#ed-save")!.addEventListener("click", async () => {
        const values = getDeptFormValues();
        if (!values) return;
        try {
          await api.settings.addDepartment({ ...values, sortOrder: 0, extendsGroupId: groupId });
          closeModal();
          const openGroupIds = captureOpenGroups(el);
          await loadSettings();
          renderSettings(el);
          restoreOpenGroups(el, openGroupIds);
          showToast("Fachbereich hinzugefügt");
        } catch (err: any) { alert(err.message); }
      });
    });
  });

  // Fachbereich aus individueller Gruppe entfernen (löschen)
  el.querySelectorAll<HTMLButtonElement>(".btn-delete-custom-dept").forEach(btn => {
    btn.addEventListener("click", async () => {
      const deptId = btn.dataset.deptId!;
      // Der Button steht sowohl in individuellen Gruppen als auch bei den eigenen
      // Fachbereichen einer Standardgruppe, deshalb über alle Gruppen suchen.
      let dept;
      for (const g of allGroups) {
        dept = g.departments.find(d => d.id === deptId);
        if (dept) break;
      }
      if (!dept) return;
      if (!confirm(`Fachbereich „${dept.nameDe}" entfernen? Vorhandene Einsätze, Kapazitäten und Wünsche zu diesem Fachbereich werden dabei gelöscht.`)) return;
      try {
        await api.settings.deleteDepartment(deptId);
        const openGroupIds = captureOpenGroups(el);
        await loadSettings();
        renderSettings(el);
        restoreOpenGroups(el, openGroupIds);
        showToast("Fachbereich entfernt");
      } catch (err: any) { alert(err.message); }
    });
  });

  // Kapazität pro Zeitraum Toggle
  el.querySelector("#toggle-kpz")?.addEventListener("change", async (e) => {
    const enabled = (e.target as HTMLInputElement).checked;
    try {
      await api.settings.toggleKapazitaetProZeitraum(enabled);
      await loadSettings();
      showToast(enabled ? "Kapazität pro Zeitraum aktiviert" : "Kapazität pro Zeitraum deaktiviert");
    } catch (err: any) {
      (e.target as HTMLInputElement).checked = !enabled;
      alert(err.message);
    }
  });

  // Vorplanung Toggle
  el.querySelector("#toggle-vorplanung")?.addEventListener("change", async (e) => {
    const enabled = (e.target as HTMLInputElement).checked;
    try {
      await api.settings.toggleVorplanung(enabled);
      await loadSettings();
      renderSettings(el);
      showToast(enabled ? "Vorplanung aktiviert" : "Vorplanung deaktiviert");
    } catch (err: any) {
      (e.target as HTMLInputElement).checked = !enabled;
      alert(err.message);
    }
  });

  // Abrechnung Toggle (nur aktivierbar, wenn das Planungs-Tool freigeschaltet ist)
  el.querySelector("#toggle-abrechnung")?.addEventListener("change", async (e) => {
    const enabled = (e.target as HTMLInputElement).checked;
    try {
      await api.settings.toggleAbrechnung(enabled);
      await loadSettings();
      showToast(enabled ? "Abrechnung aktiviert" : "Abrechnung deaktiviert");
    } catch (err: any) {
      (e.target as HTMLInputElement).checked = !enabled;
      alert(err.message);
    }
  });

  // Vorplanung: Standardwerte für neue, manuell angelegte Einsätze
  const wireVorplanungDefaults = () => {
    const visibleEl = el.querySelector<HTMLInputElement>("#toggle-vorplanung-default-visible");
    const confirmedEl = el.querySelector<HTMLInputElement>("#toggle-vorplanung-default-confirmed");
    if (!visibleEl || !confirmedEl) return;
    const push = async () => {
      const defaultVisible = visibleEl.checked;
      const defaultPreliminary = !confirmedEl.checked;
      try {
        await api.settings.updateVorplanungDefaults(defaultVisible, defaultPreliminary);
        await loadSettings();
        showToast("Standardwerte gespeichert");
      } catch (err: any) {
        alert(err.message);
        await loadSettings();
      }
    };
    visibleEl.addEventListener("change", push);
    confirmedEl.addEventListener("change", push);
  };
  wireVorplanungDefaults();

  // Schule löschen
  attachDeleteSchoolListeners(el);

  // Schulträger. Eine verbundene Schule kann sich erst nach dem Lösen vom Träger löschen.
  renderSchoolCarrierCard(el.querySelector("#school-carrier-card") as HTMLElement, (status) => {
    const content = el.querySelector("#delete-school-content") as HTMLElement | null;
    if (!content) return;
    if (!status.carrier) {
      // Gerade vom Träger gelöst: Löschen wieder anbieten
      if (content.dataset.carrierBlocked) { delete content.dataset.carrierBlocked; attachDeleteSchoolListeners(el); }
      return;
    }
    content.dataset.carrierBlocked = "1";
    content.innerHTML = `<p style="font-size:.875rem;color:var(--gray-600);margin:0">${status.carrier.isFoundingSchool
      ? "Deine Schule ist Stammschule eines Schulträgers. Sie kann erst gelöscht werden, wenn der Schulträger gelöscht ist."
      : "Deine Schule ist mit einem Schulträger verbunden. Sie kann erst gelöscht werden, wenn du sie oben vom Schulträger gelöst hast."}</p>`;
  });
}

function attachDeleteSchoolListeners(el: HTMLElement) {
  const content = el.querySelector("#delete-school-content") as HTMLElement;
  let savedPassword = "";

  function renderPhase1() {
    content.innerHTML = `
      <div style="background:#fff7f7;border:1px solid #fecaca;border-radius:.5rem;padding:1rem;margin-bottom:1rem">
        <p style="font-weight:600;color:#dc2626;margin:0 0 .5rem 0">Achtung: Diese Aktion ist unwiderruflich!</p>
        <p style="color:var(--gray-600);margin:0;font-size:.875rem">Alle Daten deiner Schule werden dauerhaft gelöscht – Klassen, Schüler, Einsatzstellen, Planungen und alle Benutzerkonten. Diese Aktion kann nicht rückgängig gemacht werden.</p>
      </div>
      <button class="btn" id="btn-delete-phase1" style="background:#dc2626;color:#fff;border-color:#dc2626">Ja, ich möchte die Schule löschen</button>`;

    content.querySelector("#btn-delete-phase1")!.addEventListener("click", renderPhase2);
  }

  function renderPhase2() {
    content.innerHTML = `
      <div style="background:#fff7f7;border:1px solid #fecaca;border-radius:.5rem;padding:1rem;margin-bottom:1rem">
        <p style="font-weight:600;color:#dc2626;margin:0 0 .5rem 0">Bitte bestätige deine Identität</p>
        <p style="color:var(--gray-600);margin:0;font-size:.875rem">Gib dein aktuelles Passwort ein, um fortzufahren.</p>
      </div>
      <div class="form-group" style="max-width:24rem">
        <label>Passwort</label>
        <input id="delete-password" type="password" placeholder="Dein aktuelles Passwort" autocomplete="current-password" />
        <div id="delete-pw-error" style="color:#dc2626;font-size:.8125rem;margin-top:.25rem;display:none"></div>
      </div>
      <div style="display:flex;gap:.5rem;margin-top:.5rem">
        <button class="btn btn-secondary" id="btn-delete-cancel2">Abbrechen</button>
        <button class="btn" id="btn-delete-phase2" style="background:#dc2626;color:#fff;border-color:#dc2626">Weiter</button>
      </div>`;

    content.querySelector("#btn-delete-cancel2")!.addEventListener("click", renderPhase1);
    content.querySelector("#btn-delete-phase2")!.addEventListener("click", () => {
      const pw = (content.querySelector("#delete-password") as HTMLInputElement).value;
      if (!pw) {
        const err = content.querySelector("#delete-pw-error") as HTMLElement;
        err.textContent = "Bitte gib dein Passwort ein.";
        err.style.display = "block";
        return;
      }
      savedPassword = pw;
      renderPhase3();
    });
  }

  function renderPhase3(errorMsg?: string) {
    content.innerHTML = `
      <div style="background:#fff7f7;border:1px solid #fecaca;border-radius:.5rem;padding:1rem;margin-bottom:1rem">
        <p style="font-weight:600;color:#dc2626;margin:0 0 .5rem 0">Bist du wirklich sicher?</p>
        <p style="color:var(--gray-600);margin:0;font-size:.875rem">Du bist dabei, die Schule <strong>${esc(settings?.name ?? "")}</strong> und alle zugehörigen Daten endgültig zu löschen. Dies kann nicht rückgängig gemacht werden.</p>
      </div>
      ${errorMsg ? `<div style="color:#dc2626;font-size:.875rem;margin-bottom:.75rem">${esc(errorMsg)}</div>` : ""}
      <div style="display:flex;gap:.5rem">
        <button class="btn btn-secondary" id="btn-delete-cancel3">Abbrechen</button>
        <button class="btn" id="btn-delete-confirm" style="background:#dc2626;color:#fff;border-color:#dc2626">Schule endgültig löschen</button>
      </div>`;

    content.querySelector("#btn-delete-cancel3")!.addEventListener("click", renderPhase2);
    content.querySelector("#btn-delete-confirm")!.addEventListener("click", async () => {
      const btn = content.querySelector("#btn-delete-confirm") as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = "Wird gelöscht…";
      try {
        await api.settings.deleteSchool(savedPassword);
        localStorage.clear();
        window.location.href = "/";
      } catch (err: any) {
        savedPassword = "";
        if (err.status === 401 || (err.message && err.message.toLowerCase().includes("passwort"))) {
          renderPhase2();
          const errEl = content.querySelector("#delete-pw-error") as HTMLElement;
          errEl.textContent = "Passwort ist falsch. Bitte erneut eingeben.";
          errEl.style.display = "block";
        } else {
          renderPhase3(err.message || "Ein Fehler ist aufgetreten.");
        }
      }
    });
  }

  renderPhase1();
}

function attachGroupPoolListeners(el: HTMLElement, group: DepartmentGroup) {
  const gid = group.id;
  const poolHours = group.poolHours!;
  const counter = el.querySelector(`#pool-counter-g-${gid}`) as HTMLElement;
  const saveBtn = el.querySelector(`.btn-save-pool-g[data-group-id="${gid}"]`) as HTMLButtonElement;
  if (!counter || !saveBtn) return;

  function updateCounter() {
    const inputs = Array.from(el.querySelectorAll(`.pool-input-g[data-group-id="${gid}"]`)) as HTMLInputElement[];
    const sum = inputs.reduce((acc, inp) => acc + (parseInt(inp.value) || 0), 0);

    inputs.forEach(inp => {
      const deptId = inp.dataset.deptId!;
      const dept = group.departments.find(d => d.id === deptId);
      const base = dept?.requiredHours ?? 0;
      const poolVal = parseInt(inp.value) || 0;
      const totalCell = el.querySelector(`.pool-total-cell-g[data-dept-id="${CSS.escape(deptId)}"][data-group-id="${gid}"]`) as HTMLElement;
      if (totalCell) totalCell.textContent = `${base + poolVal} h`;
    });

    const ok = sum === poolHours;
    counter.textContent = `${sum} / ${poolHours} h`;
    counter.style.background = ok ? "#dcfce7" : sum > poolHours ? "#fee2e2" : "var(--gray-100)";
    counter.style.color = ok ? "#15803d" : sum > poolHours ? "#dc2626" : "var(--gray-600)";
    saveBtn.disabled = !ok;
  }

  el.querySelectorAll(`.pool-input-g[data-group-id="${gid}"]`).forEach(inp => {
    inp.addEventListener("input", updateCounter);
  });
  updateCounter();

  saveBtn.addEventListener("click", async () => {
    const inputs = Array.from(el.querySelectorAll(`.pool-input-g[data-group-id="${gid}"]`)) as HTMLInputElement[];
    const allocations: Record<string, number> = {};
    inputs.forEach(inp => { allocations[inp.dataset.deptId!] = parseInt(inp.value) || 0; });
    try {
      await api.settings.savePoolDistribution(gid, allocations);
      await loadSettings();
      if (currentEl) renderSettings(currentEl);
      showToast("Pool-Verteilung gespeichert");
    } catch (err: any) { alert(err.message); }
  });
}

async function saveSchoolData() {
  const el = currentEl!;
  const data = {
    address: (el.querySelector("#s-address") as HTMLInputElement).value.trim() || null,
    plz: (el.querySelector("#s-plz") as HTMLInputElement).value.trim() || null,
    ort: (el.querySelector("#s-ort") as HTMLInputElement).value.trim() || null,
    phone: (el.querySelector("#s-phone") as HTMLInputElement).value.trim() || null,
    email: (el.querySelector("#s-email") as HTMLInputElement).value.trim() || null,
  };
  try {
    await api.settings.update(data);
    await loadSettings();
    renderSettings(el);
    showToast("Schuldaten gespeichert");
  } catch (err: any) { alert(err.message); }
}

function deptFormHtml(dept?: Department): string {
  return `
    <div class="form-grid" style="margin-bottom:1rem">
      <div class="form-group col-span-2">
        <label>Name</label>
        <input id="df-name" value="${esc(dept?.nameDe || "")}" placeholder="z.B. Chirurgie" />
      </div>
      <div class="form-group">
        <label>Kürzel <span class="text-muted">(max. 10 Zeichen)</span></label>
        <input id="df-short" value="${esc(dept?.shortName || "")}" placeholder="z.B. Chir" maxlength="10" />
      </div>
      <div class="form-group">
        <label>Farbe</label>
        <input id="df-color" type="color" value="${esc(dept?.color || "#6b7280")}" style="width:100%;height:2.25rem;padding:.125rem .25rem;border:1px solid var(--gray-300);border-radius:.375rem;cursor:pointer" />
      </div>
      <div class="form-group col-span-2">
        <label>Pflichtstunden pro Schüler</label>
        <input id="df-hours" type="number" min="0" value="${dept?.requiredHours ?? 0}" placeholder="0" />
      </div>
    </div>`;
}

function getDeptFormValues(): { nameDe: string; shortName: string; color: string; requiredHours: number } | null {
  const mb = modalBody();
  const nameDe = (mb.querySelector("#df-name") as HTMLInputElement).value.trim();
  const shortName = (mb.querySelector("#df-short") as HTMLInputElement).value.trim();
  const color = (mb.querySelector("#df-color") as HTMLInputElement).value.trim();
  const requiredHours = parseInt((mb.querySelector("#df-hours") as HTMLInputElement).value) || 0;

  if (!nameDe) { alert("Bitte gib einen Namen ein"); return null; }
  if (!shortName) { alert("Bitte gib ein Kürzel ein"); return null; }
  if (!color) { alert("Bitte wähle eine Farbe"); return null; }
  return { nameDe, shortName, color, requiredHours };
}

function openDepartmentEditModal(dept: Department) {
  openModal(`Fachbereich bearbeiten`, `
    ${deptFormHtml(dept)}
    <div class="form-actions">
      <button class="btn btn-secondary" id="de-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="de-save">Speichern</button>
    </div>`);

  const mb = modalBody();
  mb.querySelector("#de-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#de-save")!.addEventListener("click", async () => {
    const values = getDeptFormValues();
    if (!values) return;
    try {
      await api.settings.updateDepartment(dept.id, values);
      closeModal();
      const openGroupIds = captureOpenGroups(currentEl!);
      await loadSettings();
      if (currentEl) renderSettings(currentEl);
      restoreOpenGroups(currentEl!, openGroupIds);
      showToast("Fachbereich gespeichert");
    } catch (err: any) { alert(err.message); }
  });
}


function showToast(message: string) {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add("toast--visible"));
  setTimeout(() => {
    toast.classList.remove("toast--visible");
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

export async function render(el: HTMLElement) {
  currentEl = el;
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;
  await loadSettings();
  renderSettings(el);
}
